import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { tokenStore } from "../lib/tokenStore";
import { Plus, Trash2, BookOpen, Download, BookMarked, MoreHorizontal, Upload, FileText, Loader2, Check } from "lucide-react";

const Dashboard = () => {
  const [stories, setStories] = useState([]);
  const [themes, setThemes] = useState({});
  const [characters, setCharacters] = useState({});
  const [loading, setLoading] = useState(true);
  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [impTitle, setImpTitle] = useState("");
  const [impBody, setImpBody] = useState("");
  const [impCharIds, setImpCharIds] = useState([]);
  const [impThemeId, setImpThemeId] = useState("");
  const [impIntensity, setImpIntensity] = useState("steamy");
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const charList = useMemo(() => Object.values(characters), [characters]);
  const themeList = useMemo(() => Object.values(themes), [themes]);

  const previewChapterCount = useMemo(() => {
    if (!impBody.trim()) return 0;
    const re = /^[ \t]*(?:#{1,3}[ \t]+)?(?:chapter|ch\.?)[ \t]+([IVXLCDM\d]+|one|two|three|four|five|six|seven|eight|nine|ten)\b/gim;
    const matches = impBody.match(re);
    return matches ? matches.length : 1;
  }, [impBody]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, c] = await Promise.all([
        api.get("/stories"),
        api.get("/themes"),
        api.get("/characters"),
      ]);
      setStories(s.data);
      setThemes(Object.fromEntries(t.data.map(x => [x.id, x])));
      setCharacters(Object.fromEntries(c.data.map(x => [x.id, x])));
    } catch (err) {
      toast.error("Could not load your library");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deleteStory = async (id) => {
    try {
      await api.delete(`/stories/${id}`);
      toast.success("Story deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const downloadStory = async (storyId, format) => {
    try {
      const token = tokenStore.getToken();
      const resp = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stories/${storyId}/download?format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const disposition = resp.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `story.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Saved as ${filename}`);
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const resetImport = () => {
    setImpTitle(""); setImpBody(""); setImpCharIds([]); setImpThemeId(""); setImpIntensity("steamy");
  };

  const openImport = () => {
    resetImport();
    setImportOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("File must be under 1 MB"); return; }
    const text = await file.text();
    setImpBody(text);
    if (!impTitle && file.name) {
      setImpTitle(file.name.replace(/\.(md|txt|markdown)$/i, ""));
    }
  };

  const toggleImpChar = (id) => {
    setImpCharIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length < 5 ? [...prev, id] : prev));
  };

  const submitImport = async () => {
    if (!impTitle.trim()) { toast.error("Title is required"); return; }
    if (!impBody.trim()) { toast.error("Story body is empty"); return; }
    if (impCharIds.length === 0) { toast.error("Pick at least one character"); return; }
    if (!impThemeId) { toast.error("Pick a theme"); return; }
    setImporting(true);
    try {
      const r = await api.post("/stories/import", {
        title: impTitle.trim(),
        character_ids: impCharIds,
        theme_id: impThemeId,
        intensity: impIntensity,
        vibes: [],
        body: impBody,
      }, { timeout: 120000 });
      toast.success(`Imported as ${r.data.chapter_count ?? ""} chapter${(r.data.chapter_count ?? 1) === 1 ? "" : "s"}.`);
      setImportOpen(false);
      navigate(`/story/${r.data.id}`);
    } catch (err) {
      let msg;
      if (err.response) {
        const d = err.response.data?.detail;
        if (Array.isArray(d)) {
          msg = d.map(x => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ");
        } else if (typeof d === "string") {
          msg = d;
        } else {
          msg = `HTTP ${err.response.status} — ${err.response.statusText || "import failed"}`;
        }
      } else if (err.code === "ECONNABORTED") {
        msg = "Import took too long — try a shorter body or split it into fewer chapters.";
      } else if (err.message) {
        msg = `Network: ${err.message}`;
      } else {
        msg = "Import failed";
      }
      // Log full error object for debugging via DevTools console
      console.error("Import error:", err); // eslint-disable-line no-console
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-stone-950 grain-overlay" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-16 flex-wrap gap-6">
          <div>
            <div className="overline text-xs font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-3">
              Your library
            </div>
            <h1 className="font-serif-heading text-5xl md:text-6xl font-light text-stone-100 leading-none">
              My Stories
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              data-testid="import-story-btn"
              onClick={openImport}
              className="rounded-none h-12 px-4 text-stone-300 hover:text-stone-100 border border-stone-800 hover:border-stone-600 hover:bg-transparent font-sans-ui text-xs uppercase tracking-[0.25em]"
            >
              <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Import
            </Button>
            <Link to="/create" data-testid="new-story-btn">
              <Button className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-6 font-sans-ui text-xs uppercase tracking-[0.25em]">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Begin new story
              </Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="text-stone-500 font-serif-reading italic">Gathering your pages…</div>
        ) : stories.length === 0 ? (
          <div className="border border-stone-800 p-16 text-center" data-testid="empty-state">
            <BookOpen className="w-8 h-8 text-stone-700 mx-auto mb-6" strokeWidth={1.2} />
            <h3 className="font-serif-heading text-3xl text-stone-200 mb-4">A blank page.</h3>
            <p className="font-serif-reading text-stone-400 italic mb-8">Your first story awaits its first line.</p>
            <Link to="/create">
              <Button className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-8 font-sans-ui text-xs uppercase tracking-[0.25em]">
                Begin writing
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {stories.map((s, i) => {
              const theme = themes[s.theme_id];
              const chars = s.character_ids.map(id => characters[id]).filter(Boolean);
              return (
                <div key={s.id} data-testid={`story-card-${s.id}`}
                  className={`group border border-stone-800 bg-stone-900/40 hover:border-rose-900/60 transition-colors fade-up-delay-${Math.min(i+1, 3)}`}>
                  <Link to={`/story/${s.id}`} className="block p-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="overline text-[10px] font-sans-ui uppercase tracking-[0.3em] text-rose-800/80">
                        {theme?.name || "Untitled theme"}
                      </div>
                      {s.ended && (
                        <span data-testid={`fin-badge-${s.id}`}
                          className="font-serif-heading italic text-xs text-rose-300 border border-rose-900/60 px-2.5 py-0.5">
                          Fin
                        </span>
                      )}
                    </div>
                    <h3 className="font-serif-heading text-3xl text-stone-100 leading-tight mb-5 group-hover:text-rose-100 transition">
                      {s.title}
                    </h3>
                    <div className="font-serif-reading text-stone-400 text-sm italic mb-6 leading-relaxed">
                      featuring {chars.map(c => c.name).join(" & ") || "unnamed figures"}
                    </div>
                    <div className="hairline mb-5"></div>
                    <div className="flex items-center justify-between font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">
                      <span>{s.chapter_count} chapter{s.chapter_count !== 1 ? "s" : ""}</span>
                      <span className="capitalize">{s.intensity}</span>
                    </div>
                  </Link>
                  <div className="px-8 pb-6 flex items-center justify-between">
                    <Link to={`/story/${s.id}`} className="font-sans-ui text-xs uppercase tracking-[0.25em] text-stone-300 hover:text-rose-200 transition">
                      {s.ended ? "Read again" : "Continue reading"} →
                    </Link>
                    <div className="flex items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`story-menu-${s.id}`} className="text-stone-600 hover:text-stone-200 transition p-1">
                            <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-stone-950 border-stone-800 rounded-none w-56">
                          <DropdownMenuItem
                            data-testid={`download-pdf-${s.id}`}
                            disabled={s.chapter_count === 0}
                            onClick={() => downloadStory(s.id, "pdf")}
                            className="font-sans-ui text-xs uppercase tracking-[0.15em]"
                          >
                            <BookMarked className="w-3.5 h-3.5 mr-2" /> Download as PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`download-md-${s.id}`}
                            disabled={s.chapter_count === 0}
                            onClick={() => downloadStory(s.id, "md")}
                            className="font-sans-ui text-xs uppercase tracking-[0.15em]"
                          >
                            <BookMarked className="w-3.5 h-3.5 mr-2" /> Download as Markdown
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            data-testid={`download-txt-${s.id}`}
                            disabled={s.chapter_count === 0}
                            onClick={() => downloadStory(s.id, "txt")}
                            className="font-sans-ui text-xs uppercase tracking-[0.15em]"
                          >
                            <Download className="w-3.5 h-3.5 mr-2" /> Download as plain text
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-story-${s.id}`} className="text-stone-600 hover:text-rose-700 transition p-1">
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-stone-950 border-stone-800 rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif-heading text-2xl text-stone-100">Delete this story?</AlertDialogTitle>
                            <AlertDialogDescription className="font-serif-reading text-stone-400">
                              All chapters of <em>{s.title}</em> will be permanently deleted. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-none bg-stone-900 border-stone-800 text-stone-300 font-sans-ui text-xs uppercase tracking-[0.2em]">Keep</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteStory(s.id)} className="rounded-none bg-rose-900 hover:bg-rose-800 text-rose-50 font-sans-ui text-xs uppercase tracking-[0.2em]">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Import story dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => !importing && setImportOpen(v)}>
        <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
              Import a story
            </DialogTitle>
          </DialogHeader>
          <p className="font-serif-reading italic text-stone-400 text-sm -mt-2 leading-relaxed">
            Bring prose you&rsquo;ve written elsewhere into your library. Chapters split automatically on
            <span className="font-sans-ui text-[11px] ml-1">&ldquo;Chapter N&rdquo;</span> headings;
            if none are found it imports as a single chapter.
          </p>

          <div className="space-y-5 mt-2">
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Title</Label>
              <Input
                data-testid="import-title-input"
                value={impTitle}
                onChange={(e) => setImpTitle(e.target.value)}
                placeholder="The name you want it kept under"
                className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-heading text-lg focus:ring-rose-900"
              />
            </div>

            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Story body</Label>
              <div className="mt-2 flex items-center gap-3 mb-2">
                <label className="inline-flex items-center gap-2 font-sans-ui text-[11px] uppercase tracking-[0.22em] text-stone-400 hover:text-rose-200 border border-stone-800 hover:border-rose-800 px-3 py-2 transition cursor-pointer">
                  <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Upload .txt or .md
                  <input
                    type="file"
                    accept=".txt,.md,.markdown,text/plain,text/markdown"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                </label>
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-600">or paste below</span>
              </div>
              <Textarea
                data-testid="import-body-input"
                value={impBody}
                onChange={(e) => setImpBody(e.target.value)}
                placeholder={"The hotel was the kind Elena would never choose…\n\n## Chapter II\n\nShe returned the next evening…"}
                className="bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading text-sm leading-relaxed focus:ring-rose-900 min-h-[200px]"
              />
              {impBody.trim() && (
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-2">
                  {previewChapterCount} chapter{previewChapterCount === 1 ? "" : "s"} detected &middot; {impBody.trim().split(/\s+/).length.toLocaleString()} words
                </div>
              )}
            </div>

            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Cast <span className="text-stone-600 normal-case tracking-normal ml-1">(choose 1–5 — these must already exist in your library)</span>
              </Label>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                {charList.map(c => {
                  const sel = impCharIds.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`import-char-${c.id}`}
                      onClick={() => toggleImpChar(c.id)}
                      className={`text-left p-2.5 border transition ${sel ? "border-rose-800 bg-rose-950/25" : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-serif-heading text-sm ${sel ? "text-rose-100" : "text-stone-200"}`}>{c.name}</span>
                        {sel && <Check className="w-3.5 h-3.5 text-rose-400 shrink-0" strokeWidth={1.5} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Theme</Label>
                <select
                  data-testid="import-theme-select"
                  value={impThemeId}
                  onChange={(e) => setImpThemeId(e.target.value)}
                  className="mt-2 w-full bg-stone-900 border border-stone-800 rounded-none h-11 px-3 text-stone-100 font-serif-reading text-sm focus:outline-none focus:ring-1 focus:ring-rose-900"
                >
                  <option value="">— choose —</option>
                  {themeList.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Intensity</Label>
                <select
                  data-testid="import-intensity-select"
                  value={impIntensity}
                  onChange={(e) => setImpIntensity(e.target.value)}
                  className="mt-2 w-full bg-stone-900 border border-stone-800 rounded-none h-11 px-3 text-stone-100 font-serif-reading text-sm focus:outline-none focus:ring-1 focus:ring-rose-900"
                >
                  <option value="story">Story Only</option>
                  <option value="sweet">Sweet</option>
                  <option value="mild">Suggestive</option>
                  <option value="steamy">Steamy</option>
                  <option value="explicit">Explicit</option>
                  <option value="kink">Kink (BDSM)</option>
                </select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              data-testid="import-cancel-btn"
              disabled={importing}
              onClick={() => setImportOpen(false)}
              className="rounded-none h-10 px-4 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              Cancel
            </Button>
            <Button
              data-testid="import-submit-btn"
              disabled={importing}
              onClick={submitImport}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-10 px-5 font-sans-ui text-[11px] uppercase tracking-[0.25em]"
            >
              {importing ? (
                <span className="inline-flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> Importing…</span>
              ) : "Import into library"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
